import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Activity, TrendingUp, Users, DollarSign, Zap, Flame, Bot, Mail, Building2, ArrowUpRight,
  ShieldAlert, RefreshCcw, Rocket, Target, Satellite, Radar, Gauge,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { LineArea } from '@/components/charts/LineArea';
import { BarRow } from '@/components/charts/BarRow';
import { ProgressRing } from '@/components/charts/ProgressRing';
import { MissionTimer } from '@/components/admin/MissionTimer';
import { StarfieldBG } from '@/components/admin/StarfieldBG';
import { PaymentRailsCard } from '@/components/admin/PaymentRailsCard';

// Local helpers --------------------------------------------------------------
const fmtMoney = (n, digits = 0) =>
  `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: digits })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString();

// Sci-fi stat panel ----------------------------------------------------------
const StatPanel = ({ label, value, subvalue, icon: Icon, color = '#39FF14', testid, accentLabel }) => (
  <div data-testid={testid} className="relative overflow-hidden rounded-xl border border-white/5 bg-black/40 backdrop-blur p-5">
    <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} />
    <div className="absolute -top-10 -right-10 h-28 w-28 rounded-full blur-3xl pointer-events-none" style={{ background: `${color}1A` }} />
    <div className="flex items-start justify-between">
      <div className="h-9 w-9 rounded-lg grid place-items-center bg-white/[0.03] border border-white/10" style={{ boxShadow: `inset 0 0 12px ${color}22` }}>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      {accentLabel && (
        <span className="text-[9px] uppercase tracking-widest mono px-2 py-0.5 rounded-full border" style={{ color, borderColor: `${color}55` }}>
          {accentLabel}
        </span>
      )}
    </div>
    <div className="display text-3xl font-semibold mt-3 tabular-nums tracking-tight" style={{ color }}>
      {value}
    </div>
    <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mt-1">{label}</div>
    {subvalue && <div className="text-[11px] text-white/60 mt-2">{subvalue}</div>}
  </div>
);

// Section header primitive --------------------------------------------------
const SectionCard = ({ eyebrow, title, children, right, accent = '#FF6B1A' }) => (
  <div className="relative overflow-hidden rounded-xl border border-white/5 bg-black/40 backdrop-blur p-5">
    <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
    <div className="flex items-start justify-between gap-3 mb-4">
      <div>
        <span className="text-[9px] uppercase tracking-[0.3em] mono" style={{ color: accent }}>// {eyebrow}</span>
        <h2 className="display text-base font-semibold mt-1">{title}</h2>
      </div>
      {right}
    </div>
    {children}
  </div>
);

// Live mission-time stat with running clock ---------------------------------
export default function Admin() {
  const { user } = useAuth();

  const { data: adminMe, isLoading: adminMeLoading, isError: adminMeError } = useQuery({
    queryKey: ['admin-me', user?.user_id],
    queryFn: async () => (await api.get('/admin/me')).data,
    retry: false,
  });

  const isAdmin = !!adminMe?.user;

  const { data: overview, refetch: refetchOverview, isFetching: overviewFetching } = useQuery({
    queryKey: ['admin-overview'],
    queryFn: async () => (await api.get('/admin/overview')).data,
    enabled: isAdmin,
    refetchInterval: 15_000,
  });

  const { data: recent, refetch: refetchRecent } = useQuery({
    queryKey: ['admin-recent'],
    queryFn: async () => (await api.get('/admin/recent?limit=20')).data,
    enabled: isAdmin,
    refetchInterval: 15_000,
  });

  if (adminMeLoading) {
    return <div className="max-w-6xl mx-auto px-4 pt-16 text-white/60">Verifying admin session…</div>;
  }

  if (adminMeError || !isAdmin) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-16 pb-24">
        <div className="relative overflow-hidden rounded-2xl border border-white/5 bg-black/40 backdrop-blur p-8 text-center">
          <StarfieldBG density={60} />
          <ShieldAlert className="h-10 w-10 text-[#FF6B1A] mx-auto mb-4 relative" />
          <h1 className="display text-2xl font-semibold relative">Mission Control: Admin only</h1>
          <p className="text-white/65 mt-2 max-w-md mx-auto relative">
            This cockpit is reserved for the founding crew. Authenticate with an allowlisted admin account to continue.
          </p>
          <div className="mt-6 flex justify-center gap-3 relative">
            <Link to="/login" className="btn-primary rounded-xl px-5 py-2.5 text-sm" data-testid="admin-gate-login">Login</Link>
            <Link to="/" className="btn-secondary rounded-xl px-5 py-2.5 text-sm">Back home</Link>
          </div>
        </div>
      </div>
    );
  }

  const users = overview?.users;
  const revenue = overview?.revenue;
  const activity = overview?.activity;
  const funnel = overview?.funnel;
  const topAgents = overview?.top_agents_30d || [];
  const leads = overview?.leads;
  const daily = overview?.daily_series;
  const goal = overview?.goal;

  const topAgentsMax = topAgents.length ? Math.max(...topAgents.map((a) => a.messages || 0)) : 0;

  return (
    <div className="relative min-h-[70vh]">
      {/* ---------- Sci-fi backdrop ---------- */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(57,255,20,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 60%, rgba(255,107,26,0.08) 0%, transparent 60%)' }} />
        <StarfieldBG density={120} speed={0.02} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-24">
        {/* ===================== HEADER / MISSION STATUS ===================== */}
        <div className="relative overflow-hidden rounded-2xl border border-white/5 bg-black/45 backdrop-blur p-6 mb-6">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#39FF14] to-transparent" />
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] mono text-[#39FF14]">
                <Satellite className="h-3 w-3" /> // revcore.cockpit · v2.0
              </div>
              <h1 className="display text-3xl md:text-4xl font-semibold mt-2" data-testid="admin-title">
                Mission Control
              </h1>
              <p className="text-white/60 mt-2 text-sm max-w-xl">
                Live telemetry across activation, revenue, and funnel. Auto-refresh every 15s. Target: $1M PAT.
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-[#39FF14]/35 text-[#39FF14] bg-[#39FF14]/5 inline-flex items-center gap-2 mono" data-testid="admin-live-indicator">
                <span className="h-1.5 w-1.5 rounded-full bg-[#39FF14] pulse-green" />
                {overviewFetching ? 'uplinking…' : 'live uplink'}
              </span>
              <MissionTimer etaIso={goal?.goal_eta_iso} startIso={overview?.generated_at} label="Mission " />
              <button
                onClick={() => { refetchOverview(); refetchRecent(); toast.success('Telemetry refreshed'); }}
                data-testid="admin-refresh-button"
                className="btn-secondary rounded-lg px-3 py-1.5 text-[11px] inline-flex items-center gap-1"
              >
                <RefreshCcw className="h-3.5 w-3.5" /> Refresh
              </button>
            </div>
          </div>
        </div>

        {/* ===================== KPI STAT STRIP ===================== */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatPanel
            testid="admin-mrr-card"
            label="Monthly recurring"
            value={fmtMoney(revenue?.estimated_mrr)}
            subvalue={`${users?.plans?.pro || 0} Pro · ${users?.plans?.enterprise || 0} Enterprise`}
            icon={DollarSign}
            color="#39FF14"
            accentLabel="MRR"
          />
          <StatPanel
            testid="admin-arr-card"
            label="Annualized run-rate"
            value={fmtMoney(revenue?.estimated_arr)}
            subvalue={`PAT @ 30%: ${fmtMoney(goal?.current_pat_run_rate)}`}
            icon={TrendingUp}
            color="#FF6B1A"
            accentLabel="ARR"
          />
          <StatPanel
            testid="admin-total-users-card"
            label="Signups"
            value={fmtNum(users?.total)}
            subvalue={`+${fmtNum(users?.d7)} / 7d · +${fmtNum(users?.d1)} / 24h`}
            icon={Users}
            color="#39FF14"
            accentLabel="CREW"
          />
          <StatPanel
            testid="admin-messages-card"
            label="Messages routed"
            value={fmtNum(activity?.messages_total)}
            subvalue={`+${fmtNum(activity?.messages_d1)} / 24h`}
            icon={Activity}
            color="#FF6B1A"
            accentLabel="OPS"
          />
        </div>

        {/* ===================== $1M PAT COUNTDOWN GOAL CAPSULE ===================== */}
        <div className="relative overflow-hidden rounded-2xl border border-white/5 bg-black/45 backdrop-blur p-6 mb-6">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#39FF14] to-transparent" />
          <div className="absolute -right-16 -bottom-16 h-64 w-64 rounded-full bg-[#39FF14]/10 blur-3xl pointer-events-none" />
          <div className="absolute -left-16 -top-16 h-56 w-56 rounded-full bg-[#FF6B1A]/10 blur-3xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-[auto_1fr] gap-8 items-center">
            <div className="flex items-center justify-center">
              <ProgressRing
                testid="admin-pat-ring"
                value={goal?.progress_pct || 0}
                size={190}
                stroke={11}
                color="#39FF14"
                label="PAT target"
                valueText={`${(goal?.progress_pct ?? 0).toFixed(1)}%`}
                sublabel={`${fmtMoney(goal?.current_pat_run_rate)} / $1M`}
              />
            </div>
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] mono text-[#39FF14]">
                <Target className="h-3 w-3" /> // mission objective
              </div>
              <h2 className="display text-2xl md:text-3xl font-semibold mt-2 leading-tight">
                First $1M in annual PAT.
              </h2>
              <p className="text-white/65 text-sm mt-2 max-w-xl">
                Target assumes a 30% PAT margin → required ARR of {fmtMoney(goal?.required_arr)}. Current trajectory below.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                <GoalPill label="Required ARR" value={fmtMoney(goal?.required_arr)} />
                <GoalPill label="Current ARR" value={fmtMoney(goal?.current_arr)} tone="green" />
                <GoalPill label="MRR gap" value={fmtMoney(goal?.required_mrr_remaining)} />
                <GoalPill
                  testid="admin-days-to-goal"
                  label="Days to $1M PAT"
                  value={goal?.days_to_goal != null ? fmtNum(goal.days_to_goal) : '—'}
                  tone="green"
                />
              </div>

              <div className="mt-5">
                <div className="flex items-center justify-between text-[11px] text-white/55 mb-1 mono">
                  <span>progress → $3.33M ARR</span>
                  <span className="text-[#39FF14]">{(goal?.progress_pct ?? 0).toFixed(1)}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.min(100, goal?.progress_pct || 0)}%`,
                      background: 'linear-gradient(90deg, #39FF14, #A8FFCB)',
                      boxShadow: '0 0 18px rgba(57,255,20,0.5)',
                      transition: 'width 900ms cubic-bezier(.2,.9,.2,1)',
                    }}
                  />
                </div>
                <p className="text-[11px] text-white/45 mt-2 mono">
                  Last 14d daily new paid: {fmtMoney(goal?.daily_new_mrr_14d, 2)}
                  {goal?.goal_eta_iso ? ` · ETA ${new Date(goal.goal_eta_iso).toLocaleDateString()}` : ' · ETA computing when first paid signal lands'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ===================== CHARTS GRID ===================== */}
        <div className="mb-6">
          <PaymentRailsCard />
        </div>

        <div className="grid lg:grid-cols-2 gap-4 mb-6">
          <SectionCard
            eyebrow="daily telemetry · 30d"
            title="Signups"
            accent="#39FF14"
            right={<div className="text-[10px] uppercase tracking-widest text-white/40 mono">{fmtNum(users?.d30)} / 30d</div>}
          >
            <LineArea
              testid="admin-chart-signups"
              data={daily?.signups || []}
              labels={daily?.labels || []}
              width={560}
              height={170}
              color="#39FF14"
            />
          </SectionCard>

          <SectionCard
            eyebrow="daily telemetry · 30d"
            title="Revenue collected (USD)"
            accent="#FF6B1A"
            right={<div className="text-[10px] uppercase tracking-widest text-white/40 mono">{fmtMoney(revenue?.total_all_time)} all-time</div>}
          >
            <LineArea
              testid="admin-chart-revenue"
              data={daily?.revenue || []}
              labels={daily?.labels || []}
              width={560}
              height={170}
              color="#FF6B1A"
              valueFormatter={(n) => `$${Math.round(n)}`}
            />
          </SectionCard>

          <SectionCard
            eyebrow="daily telemetry · 30d"
            title="Messages routed"
            accent="#39FF14"
            right={<div className="text-[10px] uppercase tracking-widest text-white/40 mono">{fmtNum(activity?.messages_total)} total</div>}
          >
            <LineArea
              testid="admin-chart-messages"
              data={daily?.messages || []}
              labels={daily?.labels || []}
              width={560}
              height={170}
              color="#39FF14"
            />
          </SectionCard>

          <SectionCard
            eyebrow="daily telemetry · 30d"
            title="Daily active users (DAU)"
            accent="#FF6B1A"
            right={<div className="text-[10px] uppercase tracking-widest text-white/40 mono">peak {fmtNum(Math.max(0, ...(daily?.dau || [0])))}</div>}
          >
            <LineArea
              testid="admin-chart-dau"
              data={daily?.dau || []}
              labels={daily?.labels || []}
              width={560}
              height={170}
              color="#FFB17A"
            />
          </SectionCard>
        </div>

        {/* ===================== AGENTS LEADERBOARD + FUNNEL ===================== */}
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          <div className="lg:col-span-2">
            <SectionCard
              eyebrow="hot agents · 30d"
              title="Top 10 agents by messages"
              accent="#39FF14"
              right={<span className="text-[10px] uppercase tracking-widest text-white/40 mono inline-flex items-center gap-1"><Radar className="h-3 w-3" /> leaderboard</span>}
            >
              {topAgents.length === 0 ? (
                <div className="py-10 text-center">
                  <div className="text-sm text-white/70">No agent traffic yet — mission not launched.</div>
                  <p className="text-xs text-white/45 mt-2 max-w-md mx-auto">
                    Copy your launch link and seed it on X / LinkedIn tonight. Anonymous visitors get 2 free tries per agent.
                  </p>
                  <button
                    onClick={async () => {
                      const url = typeof window !== 'undefined' ? window.location.origin : '';
                      try {
                        await navigator.clipboard.writeText(url);
                        toast.success('Launch link copied — post it tonight');
                      } catch {
                        toast.message('Launch link', { description: url });
                      }
                    }}
                    data-testid="admin-copy-launch-link"
                    className="btn-primary rounded-xl px-4 py-2 text-xs mt-4 inline-flex items-center gap-2"
                  >
                    <Rocket className="h-3.5 w-3.5" /> Copy launch link
                  </button>
                </div>
              ) : (
                <div className="pt-1">
                  {topAgents.map((a, i) => (
                    <BarRow
                      key={a.agent_id}
                      rank={i + 1}
                      label={a.name}
                      sub={a.category}
                      value={a.messages}
                      max={topAgentsMax}
                      color={i < 3 ? '#39FF14' : '#FF6B1A'}
                      testid={`admin-top-agent-${i}`}
                    />
                  ))}
                </div>
              )}
            </SectionCard>
          </div>

          <SectionCard
            eyebrow="conversion funnel"
            title="Signup → paid"
            accent="#FF6B1A"
            right={<Gauge className="h-4 w-4 text-[#FF6B1A]" />}
          >
            <ul className="space-y-3">
              <FunnelRow label="Signups" value={fmtNum(funnel?.signups)} pct={100} tone="green" />
              <FunnelRow
                label="Checkouts started"
                value={fmtNum(funnel?.checkouts_started)}
                pct={funnel?.signups ? Math.round((funnel.checkouts_started / funnel.signups) * 100) : 0}
                tone="orange"
              />
              <FunnelRow
                label="Checkouts paid"
                value={fmtNum(funnel?.checkouts_paid)}
                pct={funnel?.signups ? Math.round((funnel.checkouts_paid / funnel.signups) * 100) : 0}
                tone="green"
              />
              <div className="text-[11px] uppercase tracking-widest text-white/50 pt-3 mt-3 border-t border-white/5 flex items-center justify-between mono">
                <span>signup → paid</span>
                <span className="text-[#39FF14] font-medium" data-testid="admin-signup-to-paid-pct">{funnel?.signup_to_paid_pct ?? 0}%</span>
              </div>
            </ul>
          </SectionCard>
        </div>

        {/* ===================== RECENT ACTIVITY FEEDS ===================== */}
        <div className="grid lg:grid-cols-2 gap-4 mb-6">
          <SectionCard
            eyebrow="revenue events"
            title="Recent Stripe transactions"
            accent="#39FF14"
            right={<span className="text-[10px] uppercase tracking-widest text-white/40 mono">{funnel?.checkouts_started ?? 0} started</span>}
          >
            {(recent?.recent_transactions?.length ?? 0) === 0 ? (
              <div className="text-white/55 py-6 text-sm text-center">No transactions yet.</div>
            ) : (
              <ul className="divide-y divide-white/5">
                {recent.recent_transactions.slice(0, 8).map((t) => (
                  <li key={t.transaction_id} className="py-3 flex items-center gap-3">
                    <Zap className="h-4 w-4 text-[#FF6B1A]" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white/90 truncate">{t.user_email || 'anonymous'}</div>
                      <div className="text-[11px] text-white/45 truncate mono">
                        {t.package_id?.toUpperCase()} · {(t.currency || 'usd').toUpperCase()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="display text-sm font-semibold tabular-nums">${Number(t.amount || 0).toFixed(0)}</div>
                      <div className={`text-[10px] uppercase tracking-widest mono ${t.payment_status === 'paid' ? 'text-[#39FF14]' : 'text-white/45'}`}>
                        {t.payment_status || t.status || 'pending'}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          <SectionCard
            eyebrow="activation"
            title="Recent signups"
            accent="#FF6B1A"
            right={<span className="text-[10px] uppercase tracking-widest text-white/40 mono">+{users?.d7 ?? 0} / 7d</span>}
          >
            {(recent?.recent_users?.length ?? 0) === 0 ? (
              <div className="text-white/55 py-6 text-sm text-center">No signups yet.</div>
            ) : (
              <ul className="divide-y divide-white/5">
                {recent.recent_users.slice(0, 8).map((u) => (
                  <li key={u.user_id} className="py-3 flex items-center gap-3">
                    <Flame className="h-4 w-4 text-[#39FF14]" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white/90 truncate">{u.name || u.email}</div>
                      <div className="text-[11px] text-white/45 truncate mono">
                        {u.email} · {u.provider || 'email'}
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border mono ${
                      u.plan === 'pro' || u.plan === 'enterprise'
                        ? 'border-[#39FF14]/35 text-[#39FF14] bg-[#39FF14]/8'
                        : 'border-white/10 text-white/60'
                    }`}>
                      {u.plan || 'free'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>

        {/* ===================== ENTERPRISE PIPELINE ===================== */}
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          <div className="lg:col-span-2">
            <SectionCard
              eyebrow="enterprise pipeline"
              title="Recent demo requests"
              accent="#39FF14"
              right={<span className="text-[10px] uppercase tracking-widest text-white/40 mono">{leads?.total ?? 0} total</span>}
            >
              {(recent?.recent_leads?.length ?? 0) === 0 ? (
                <div className="text-white/55 py-6 text-sm text-center">No inbound leads yet.</div>
              ) : (
                <ul className="space-y-3">
                  {recent.recent_leads.slice(0, 6).map((l) => (
                    <li key={l.lead_id} className="text-xs flex items-start gap-2">
                      <Building2 className="h-3.5 w-3.5 text-[#39FF14] mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-white/85 truncate">{l.company || 'Unknown Co.'} <span className="text-white/40">·</span> {l.name}</div>
                        <div className="text-white/45 truncate">{l.email} · {l.team_size || '—'} people</div>
                        {l.use_case && <div className="text-white/55 mt-1 line-clamp-2">{l.use_case}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
          </div>

          <SectionCard
            eyebrow="email captures"
            title="Anonymous pipeline"
            accent="#FF6B1A"
          >
            <div className="flex items-center gap-3">
              <Mail className="h-6 w-6 text-[#FF6B1A]" />
              <div>
                <div className="display text-2xl font-semibold">{fmtNum(leads?.email_captures)}</div>
                <div className="text-[11px] text-white/50 mt-1">Captured when anons hit a cap.</div>
              </div>
            </div>
            <Link to="/pricing" className="btn-secondary rounded-lg px-3 py-1.5 text-xs inline-flex items-center gap-1 mt-4">
              Review pricing <ArrowUpRight className="h-3 w-3" />
            </Link>
          </SectionCard>
        </div>

        {/* Footer */}
        <div className="text-[10px] uppercase tracking-widest text-white/35 mono text-center">
          // revcore · mission control v2 · telemetry refreshed {overview?.generated_at ? new Date(overview.generated_at).toLocaleTimeString() : '—'}
        </div>
      </div>
    </div>
  );
}

// --- Helpers ----------------------------------------------------------------
function GoalPill({ label, value, tone = 'default', testid }) {
  const colors = tone === 'green'
    ? { color: '#39FF14', border: 'rgba(57,255,20,0.35)', bg: 'rgba(57,255,20,0.07)' }
    : { color: '#FFB17A', border: 'rgba(255,177,122,0.25)', bg: 'rgba(255,107,26,0.06)' };
  return (
    <div data-testid={testid} className="rounded-lg border px-3 py-2.5" style={{ borderColor: colors.border, background: colors.bg }}>
      <div className="text-[10px] uppercase tracking-widest text-white/50 mono">{label}</div>
      <div className="display text-base font-semibold mt-0.5 tabular-nums" style={{ color: colors.color }}>{value}</div>
    </div>
  );
}

function FunnelRow({ label, value, pct, tone = 'orange' }) {
  const barBg = tone === 'green' ? 'linear-gradient(90deg, #39FF14, #A8FFCB)' : 'linear-gradient(90deg, #FF6B1A, #FFB17A)';
  const glow = tone === 'green' ? '0 0 12px rgba(57,255,20,0.35)' : '0 0 12px rgba(255,107,26,0.35)';
  return (
    <li className="text-xs">
      <div className="flex items-center justify-between mb-1">
        <span className="text-white/75">{label}</span>
        <span className="mono text-white/85 tabular-nums">{value}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.min(100, pct)}%`,
            background: barBg,
            boxShadow: glow,
            transition: 'width 700ms cubic-bezier(.2,.9,.2,1)',
          }}
        />
      </div>
    </li>
  );
}
